import * as React from 'react'
import { withNamespaces } from 'react-i18next'
import { withRouter, useHistory } from 'react-router-dom'
import {
    Table,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter
} from 'reactstrap'
import { toast } from 'react-toastify'
import { useQuery, useMutation } from '@tanstack/react-query'
import { AvForm, AvField } from 'availity-reactstrap-validation'
import Spinner from 'react-bootstrap/Spinner'
import InstituteManagementService from '../../../../../../api/InstituteManagementService'

const Institutes = ({ t, entityId }) => {
    const avFormRef = React.useRef()

    const [selectedInstituteToDelete, setSelectedInstituteToDelete] =
        React.useState(null)
    const [selectedInstituteToEdit, setSelectedInstituteToEdit] =
        React.useState(null)

    const history = useHistory()

    const instituteService = new InstituteManagementService()

    const {
        isFetching: institutesAreFetching,
        refetch: refetchInstitutesList,
        data: institutesList
    } = useQuery({
        queryKey: ['entity-management-fetch-entity-institutes-query'],
        queryFn: async () => {
            return await instituteService.fetchList(entityId, {
                pageIndex: 0,
                pageSize: 100
            })
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (_error) => {
            toast(t('An error occurred while fetching institutes list.'), {
                type: 'error'
            })
        }
    })

    const {
        isLoading: createInstituteIsLoading,
        mutate: mutateInstituteCreation
    } = useMutation({
        mutationFn: async (payload) => {
            return await instituteService.create(payload)
        },
        onSuccess: () => {
            refetchInstitutesList()

            toast(t('Institute created successfully.'), {
                type: 'success'
            })
        },
        onError: () => {
            toast(t('Failed to create Institute.'), {
                type: 'error'
            })
        }
    })

    const { isLoading: editInstituteIsLoading, mutate: mutateInstituteEdit } =
        useMutation({
            mutationFn: async (payload) => {
                return await instituteService.edit(selectedInstituteToEdit.id, payload)
            },
            onSuccess: () => {
                setSelectedInstituteToEdit(null)
                refetchInstitutesList()

                toast(t('Institute updated successfully.'), {
                    type: 'success'
                })
            },
            onError: () => {
                toast(t('Failed to edit Institute.'), {
                    type: 'error'
                })
            }
        })

    const {
        isLoading: deleteInstituteIsLoading,
        mutate: mutateInstituteDelete
    } = useMutation({
        mutationFn: async (instituteId) => {
            return await instituteService.delete(instituteId)
        },
        onSuccess: () => {
            setSelectedInstituteToDelete(null)
            refetchInstitutesList()

            toast(t('Institute deleted successfully.'), {
                type: 'success'
            })
        },
        onError: () => {
            toast(t('Failed to delete institute.'), {
                type: 'error'
            })
        }
    })

    const handleCreateInstituteSubmit = (values) => {
        mutateInstituteCreation({
            ...values,
            legalEntity: entityId
        })
    }

    const handleEditInstituteSubmit = (values) => {
        mutateInstituteEdit({
            ...values,
            legalEntity: entityId
        })
    }

    const handleInstituteSubmit = (e, values) => {
        if (selectedInstituteToEdit) {
            handleEditInstituteSubmit(values.edit)
        } else {
            handleCreateInstituteSubmit(values.create)
        }
    }

    const renderModals = () => {
        if (institutesAreFetching) {
            return null
        }

        return (
            <React.Fragment>
                <Modal
                    size='lg'
                    scrollable
                    isOpen={!!selectedInstituteToDelete}
                    backdrop='static'
                >
                    <ModalHeader
                        toggle={() => setSelectedInstituteToDelete(null)}
                    >
                        {t('Delete Institute')}
                    </ModalHeader>

                    <ModalBody>
                        <p>
                            {t(
                                'Are you sure you want to delete this institute?'
                            )}
                        </p>

                        <ModalFooter>
                            <Button
                                disabled={deleteInstituteIsLoading}
                                color='primary'
                                outline
                                className='waves-effect waves-light'
                                type='button'
                                onClick={() => {
                                    setSelectedInstituteToDelete(null)
                                }}
                            >
                                {t('Cancel')}
                            </Button>

                            <Button
                                disabled={deleteInstituteIsLoading}
                                color='danger'
                                type='button'
                                className='waves-effect waves-light'
                                onClick={() =>
                                    mutateInstituteDelete(
                                        selectedInstituteToDelete
                                    )
                                }
                            >
                                {deleteInstituteIsLoading && (
                                    <Spinner
                                        animation='border'
                                        variant='white'
                                        size='sm'
                                        className='me-1'
                                    />
                                )}
                                {t('Delete')}
                            </Button>
                        </ModalFooter>
                    </ModalBody>
                </Modal>
            </React.Fragment>
        )
    }

    const renderInstitutesTable = () => {
        const items = institutesList?.institutes || []

        return items.map((institute, index) => {
            return (
                <tr key={index}>
                    <td>
                        {institute?.id === selectedInstituteToEdit?.id ? (
                            <AvField
                                placeholder={t('Institute / Branch Name')}
                                name='edit[title]'
                                type='text'
                                errorMessage={t('This field cannot be blank')}
                                className='form-control'
                                defaultValue={institute?.title}
                                validate={{
                                    required: {
                                        value: true
                                    }
                                }}
                            />
                        ) : (
                            <React.Fragment>{institute?.title}</React.Fragment>
                        )}
                    </td>
                    <td>
                        {institute?.id === selectedInstituteToEdit?.id ? (
                            <div className='table-actions'>
                                <Button
                                    disabled={editInstituteIsLoading}
                                    color='primary'
                                    type='submit'
                                    className='waves-effect waves-light me-1'
                                >
                                    {editInstituteIsLoading && (
                                        <Spinner
                                            animation='border'
                                            variant='white'
                                            size='sm'
                                            className='me-1'
                                        />
                                    )}
                                    {t('Save')}
                                </Button>
                                <Button
                                    disabled={editInstituteIsLoading}
                                    color='danger'
                                    type='button'
                                    size='sm'
                                    outline
                                    className='border-0'
                                    onClick={() => {
                                        setSelectedInstituteToEdit(null)
                                    }}
                                >
                                    <i className='ri-close-line' />
                                </Button>
                            </div>
                        ) : (
                            <div className='table-actions'>
                                <Button
                                    title={t('Edit Name')}
                                    type='button'
                                    size='sm'
                                    color='primary'
                                    outline
                                    className='border-0'
                                    onClick={(e) => {
                                        e.preventDefault()
                                        setSelectedInstituteToEdit(institute)
                                    }}
                                >
                                    <i className='ri-pencil-line' />
                                </Button>
                                <Button
                                    title={t('Departments Management')}
                                    type='button'
                                    size='sm'
                                    color='secondary'
                                    outline
                                    className='border-0'
                                    onClick={() => {
                                        history.push(
                                            `/admin/entity-management/entities/${entityId}/institutes/${institute.id}/divisions`
                                        )
                                    }}
                                >
                                    <i className='ri-git-branch-line' />
                                </Button>
                                <Button
                                    title={t('Delete Institute')}
                                    type='button'
                                    size='sm'
                                    color='danger'
                                    outline
                                    className='border-0'
                                    onClick={() =>
                                        setSelectedInstituteToDelete(
                                            institute.id
                                        )
                                    }
                                >
                                    <i className='ri-delete-bin-line' />
                                </Button>
                            </div>
                        )}
                    </td>
                </tr>
            )
        })
    }

    const renderCreationRow = () => {
        return (
            <tr key='creation-row'>
                <td>
                    <AvField
                        disabled={!!selectedInstituteToEdit}
                        placeholder={t('Institute / Branch Name')}
                        name='create[title]'
                        type='text'
                        errorMessage={t('This field cannot be blank')}
                        className='form-control'
                        validate={{
                            required: {
                                value: !selectedInstituteToEdit
                            }
                        }}
                    />
                </td>
                <td>
                    <Button
                        outline
                        color='primary'
                        type='submit'
                        className='waves-effect waves-light'
                        disabled={
                            !!selectedInstituteToEdit ||
                            createInstituteIsLoading
                        }
                    >
                        {createInstituteIsLoading && (
                            <Spinner
                                animation='border'
                                variant='secondary'
                                size='sm'
                                className='me-1'
                            />
                        )}
                        {t('Add')}
                    </Button>
                </td>
            </tr>
        )
    }

    return (
        <div>
            {renderModals()}
            <AvForm
                className='needs-validation'
                onValidSubmit={handleInstituteSubmit}
                ref={avFormRef}
            >
                <div className="table-responsive">
                    <Table>
                        <thead>
                            <tr>
                                <th>{t('Name')}</th>
                                <th style={{
                                    width: '220px'
                                }}>{t('Action')}</th>
                            </tr>
                        </thead>
                        {institutesAreFetching ? (
                            <tbody>
                                <tr>
                                    <td>
                                        <div className='dt-field dt-skeleton dt-select-list' />
                                    </td>
                                    <td>
                                        <div className='dt-field dt-skeleton dt-select-list' />
                                    </td>
                                </tr>
                                <tr>
                                    <td>
                                        <div className='dt-field dt-skeleton dt-select-list' />
                                    </td>
                                    <td>
                                        <div className='dt-field dt-skeleton dt-select-list' />
                                    </td>
                                </tr>
                            </tbody>
                        ) : (
                            <tbody>
                                {renderInstitutesTable()}
                                {renderCreationRow()}
                            </tbody>
                        )}
                    </Table>
                </div>
            </AvForm>
        </div>
    )
}

export default withNamespaces()(
    withRouter(Institutes)
);